import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeContext, type Theme } from './theme-context';

const PORTFOLIO_THEME_KEY = 'portfolio-theme';
const BRIEFINGS_THEME_KEY = 'briefings-theme';
const LEGACY_THEME_KEY = 'theme';

function getInitialTheme(storageKey: string, fallbackKey?: string): Theme {
  try {
    const saved = localStorage.getItem(storageKey) as Theme | null;
    if (saved) return saved;

    if (fallbackKey) {
      const fallback = localStorage.getItem(fallbackKey) as Theme | null;
      if (fallback) return fallback;
    }
  } catch {
    // ignore errors (e.g., during server-side rendering)
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isBriefingsRoute = location.pathname.startsWith('/briefings');

  const [portfolioTheme, setPortfolioTheme] = useState<Theme>(() => getInitialTheme(PORTFOLIO_THEME_KEY, LEGACY_THEME_KEY));
  const [briefingsTheme, setBriefingsTheme] = useState<Theme>(() => getInitialTheme(BRIEFINGS_THEME_KEY));

  const activeTheme = useMemo(() => (isBriefingsRoute ? briefingsTheme : portfolioTheme), [briefingsTheme, isBriefingsRoute, portfolioTheme]);

  useEffect(() => {
    try {
      localStorage.setItem(PORTFOLIO_THEME_KEY, portfolioTheme);
      localStorage.removeItem(LEGACY_THEME_KEY);
    } catch {
      // localStorage may be unavailable; fail silently
    }
  }, [portfolioTheme]);

  useEffect(() => {
    try {
      localStorage.setItem(BRIEFINGS_THEME_KEY, briefingsTheme);
    } catch {
      // localStorage may be unavailable; fail silently
    }
  }, [briefingsTheme]);

  useEffect(() => {
    const root = document.documentElement;
    if (activeTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [activeTheme]);

  const toggleTheme = () => {
    if (isBriefingsRoute) {
      setBriefingsTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
      return;
    }

    setPortfolioTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return <ThemeContext.Provider value={{ theme: activeTheme, toggleTheme }}>{children}</ThemeContext.Provider>;
}
